import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  centerCrop,
  Crop,
  makeAspectCrop,
  PixelCrop,
  ReactCropProps,
} from 'react-image-crop';
import {
  calculateCenteredCrop,
  clampCropToAspectRange,
  getCroppedImgFromOriginal,
  imgSrcToImage,
  isBelowMinimumSize,
  resizeImage,
} from '@/components/ImageCropEditor/index.util';
import { clamp } from '@/utils/touch';

export type AspectRange = { readonly min: number; readonly max: number };

type ImageCropEditorSettings = Omit<
  ReactCropProps,
  'onChange' | 'crop' | 'onComplete'
> & {
  readonly src: string;
  /** 크롭 화면에 그릴 이미지의 긴 변 길이(px). */
  readonly cropCanvasSize: number;
  readonly handleOnError?: (e: unknown) => void;
  /**
   * 지정하면 비율을 고정하지 않고 이 범위 안에서 자유롭게 자른다.
   * 범위를 벗어나는 크롭은 경고 대신 경계값으로 되돌려, 어긋난 결과 자체를 만들 수 없게 한다.
   */
  readonly aspectRange?: AspectRange;
};

export type ImageCropEditorProps = ReactCropProps & {
  readonly isMinimumReached: boolean;
  readonly getCroppedImage: () => string;
  readonly previewImage: string;
  readonly completedCrop?: PixelCrop;
  readonly selectedImageSize: { width: number; height: number };
};

const useImageCropEditorProps = (
  props: ImageCropEditorSettings,
): ImageCropEditorProps => {
  const {
    minWidth,
    minHeight,
    src,
    aspect = 1,
    aspectRange,
    cropCanvasSize,
    handleOnError,
  } = props;

  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  const [previewImage, setPreviewImage] = useState('');
  const [resizeScale, setResizeScale] = useState(1);
  const [isMinimumReached, setIsMinimumReached] = useState(false);

  const [selectedImageSize, setSelectedImageSize] = useState({
    width: 0,
    height: 0,
  });

  const [originalImageElement, setOriginalImageElement] =
    useState<HTMLImageElement>();

  const minAspect = aspectRange?.min;
  const maxAspect = aspectRange?.max;
  // 호출부가 매번 새 객체를 넘기더라도 크롭이 다시 초기화되지 않도록 값으로 기억해 둔다.
  const range = useMemo(
    () =>
      minAspect !== undefined && maxAspect !== undefined
        ? { min: minAspect, max: maxAspect }
        : undefined,
    [minAspect, maxAspect],
  );

  const getCroppedImage = useCallback(() => {
    if (!completedCrop || !originalImageElement) return '';

    return getCroppedImgFromOriginal(
      originalImageElement,
      completedCrop,
      resizeScale,
    );
  }, [originalImageElement, completedCrop, resizeScale]);

  useEffect(() => {
    if (!src) {
      return;
    }

    const img = imgSrcToImage(src);

    img.onload = () => {
      const { width, height } = img;
      // 세로로 긴 이미지도 팝업 안에 담기도록, 가로가 아니라 긴 변을 기준으로 줄인다.
      const scale = cropCanvasSize / Math.max(width, height);
      setResizeScale(scale);
      setPreviewImage(resizeImage(img, scale));
      setOriginalImageElement(img);

      // 자유 비율일 때는 원본 비율에서 시작하되, 허용 범위를 넘으면 경계값으로 맞춰 연다.
      const initialAspect = range
        ? clamp(width / height, range.min, range.max)
        : aspect;

      const initialCrop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 100,
          },
          initialAspect,
          width,
          height,
        ),
        width,
        height,
      );

      setCrop(initialCrop);

      setCompletedCrop({
        unit: 'px',
        ...calculateCenteredCrop(
          img.width * scale,
          img.height * scale,
          initialAspect,
        ),
      });

      const initialSetting = validate(
        calculateCenteredCrop(img.width, img.height, initialAspect),
        1,
        minWidth,
        minHeight,
        Boolean(range),
      );

      setSelectedImageSize({
        width: initialSetting.width,
        height: initialSetting.height,
      });

      setIsMinimumReached(initialSetting.isMinimumReached);
    };

    if (handleOnError) {
      img.onerror = handleOnError;
    }
  }, [src, aspect, range, cropCanvasSize, handleOnError]);

  // aspectRange 는 이 훅에서만 쓰는 설정이라, ReactCrop 으로 그대로 흘려보내지 않는다.
  const { aspectRange: _aspectRange, ...cropProps } = props;

  return {
    ...cropProps,
    aspect: range ? undefined : aspect,
    crop,
    getCroppedImage,
    isMinimumReached,
    onChange: useCallback(
      (crop) => setCrop(range ? clampCropToAspectRange(crop, range) : crop),
      [range],
    ),
    onComplete: (crop, percentageCrop) => {
      const clampedCrop = range ? clampCropToAspectRange(crop, range) : crop;
      setCompletedCrop(clampedCrop);

      const { width, height, isMinimumReached } = validate(
        clampedCrop,
        resizeScale,
        minWidth,
        minHeight,
        Boolean(range),
      );
      setSelectedImageSize({
        width,
        height,
      });
      setIsMinimumReached(isMinimumReached);
    },
    previewImage,
    completedCrop,
    selectedImageSize,
  };
};

export default useImageCropEditorProps;

const validate = (
  crop: Pick<PixelCrop, 'width' | 'height'>,
  scale: number,
  minWidth?: number,
  minHeight?: number,
  isFreeAspect = false,
): { width: number; height: number; isMinimumReached: boolean } => {
  const resizedWidth = parseInt(`${crop.width / scale}`);
  const resizedHeight = parseInt(`${crop.height / scale}`);

  return {
    width: resizedWidth,
    height: resizedHeight,
    isMinimumReached: isBelowMinimumSize(
      resizedWidth,
      resizedHeight,
      minWidth,
      minHeight,
      isFreeAspect,
    ),
  };
};
